import { ColumnTypeEnum } from "@/codegen/graphql-request"
import * as types from "@/type"
import moment from "moment-timezone"

const TIMEZONE = "Asia/Tokyo" as const

export const encodeDatetime = (value: string | null, format?: string): string => {
	if (!value) return ""

	const _format = format ?? "YYYY-MM-DD HH:mm"
	return moment(value).tz(TIMEZONE).format(_format)
}

const decodeDatetime = (value: string | null, format?: string): string => {
	if (!value) return ""

	const _format = format ?? "YYYY-MM-DD HH:mm"
	return moment.tz(value, _format, TIMEZONE).utc().toISOString()
}

const dwIndex = ["日", "月", "火", "水", "木", "金", "土"]
const encodeRepetition = (value: types.Repetition | null): string => {
	if (!value) return ""

	let items: string[] = []
	switch (value.category) {
		case "dw":
			items = value.specifier.map((s) => dwIndex[s])
			items.push(`/${value.frequency}週`)
			break
		case "d":
			items = value.specifier.map((s) => (s == 99 ? "最終日" : `${s}日`))
			items.push(`/${value.frequency}ヶ月`)
			break
		case "dd":
			items = [`${value.frequency}日おき`]
			break
		default:
			break
	}

	return items.join("")
}

const decodeRepetition = (value: string | null): types.Repetition | null => {
	if (!value) return null

	// f = frequency
	const pattern = {
		fdw: /(\d+)週/,
		fd: /(\d+)ヶ月/,
		fdd: /(\d+)日おき/,
		dw: /([月火水木金土日])/g,
		d: /(\d+)日/g,
		d99: /最終日/,
	}

	// eslint-disable-next-line prefer-const
	let [_spec, _freq] = (() => {
		const converted = value
			.replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0)) // Replace full-width numbers with half-width numbers
			.replace(/／|か月|　| /g, (s) => {
				switch (s) {
					case "／": // Replace full-width slash with half-width slash
						return "/"
					case "か月": // Replace "か月" with "ヶ月"
						return "ヶ月"
					case "　": // Remove full-width space
					case " ": // Remove half-width space
						return ""
					default:
						return s
				}
			})
		// n日おきは split できないので先に処理する
		if (converted.includes("おき")) {
			return ["", converted]
		}
		return converted.split("/")
	})()

	let category: types.Repetition["category"] | null = null
	let frequency = 1
	const specifier: number[] = []

	// n日おき
	if (_spec === "" && pattern.fdd.test(_freq)) {
		category = "dd"
		frequency = parseInt(_freq.match(pattern.fdd)?.[1] ?? "1", 10)
	} else if (pattern.d.test(_spec)) {
		category = "d"
		// global frag 付きの RegExp は前回のマッチ位置から検索を続けるので、
		// 新規に RegExp を作成する必要がある
		for (const matcher of _spec.matchAll(new RegExp(pattern.d))) {
			const day = parseInt(matcher[1], 10)
			if (day > 0) {
				specifier.push(day)
			}
		}
	} else if (_spec.includes("最終日")) {
		category = "d"
		specifier.push(99)
	} else if (pattern.dw.test(_spec)) {
		category = "dw"
		for (const matcher of _spec.matchAll(new RegExp(pattern.dw))) {
			specifier.push(dwIndex.indexOf(matcher[1]))
		}
		const freqMatcher = _freq.match(pattern.fdw)
		frequency = freqMatcher ? parseInt(freqMatcher[1], 10) : 1
	}

	if (_freq && category == "d") {
		const freqMatcher = _freq.match(new RegExp(pattern.fd))
		frequency = freqMatcher ? parseInt(freqMatcher[1], 10) : 1
	}

	if (!category) {
		return null
	}
	// d, dw の場合は specifier が必須
	if ((category === "d" || category === "dw") && specifier.length === 0) {
		return null
	}
	// 周期が 0 の場合は無効
	if (frequency === 0) {
		return null
	}

	return {
		category,
		specifier,
		frequency,
	}
}

const encodeTimePeriod = (value: types.Period): string => {
	const start = value.start ? moment(value.start).tz(TIMEZONE).format("HH:mm") : ""
	const end = value.end ? moment(value.end).tz(TIMEZONE).format("HH:mm") : ""
	return start || end ? `${start}-${end}` : ""
}

const decodeTimePeriod = (value: string | null): types.Period => {
	if (!value) return { start: null, end: null }

	const getMoment = (time: string) => {
		const m = moment.tz(time, "HH:mm", TIMEZONE).utc().set({ year: 1970, month: 0, date: 1 })
		return m.isValid() ? m : null
	}

	const [s, e] = value.split("-")
	const start = s ? getMoment(s) : null
	const end = e ? getMoment(e) : null
	if (start === null || end === null) return { start: null, end: null }

	if (end.isSameOrBefore(start)) {
		end.add(1, "d")
	}

	return { start: start.toISOString(), end: end.toISOString() }
}

export const convertToDisplay = (data: types.ColumnData, type: ColumnTypeEnum): string => {
	if (!data) return ""
	switch (type) {
		case "string":
		case "number":
			return data as string
		case "datetime":
			return encodeDatetime(data as string, "YYYY/MM/DD HH:mm")
		case "time_period":
			return encodeTimePeriod(data as types.Period)
		case "repetition":
			return encodeRepetition(data as types.Repetition)
		default:
			return data as string
	}
}

export const convertToStore = (data: string, type: ColumnTypeEnum): types.ColumnData => {
	switch (type) {
		case "string":
		case "number":
			return data
		case "datetime":
			return decodeDatetime(data)
		case "time_period":
			return decodeTimePeriod(data)
		case "repetition":
			return decodeRepetition(data)
		default:
			return data
	}
}
